import React, { useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/constants/featureRegistry";
import { useOrderAttention } from "@/context/OrderAttentionContext";
import { Button } from "@/components/ui/button";
import { REQUEST_LABEL } from "@/lib/orderCancellation";
import { ApproveCancellationDialog, RejectCancellationDialog } from "@/components/admin/orders/CancellationDecisionDialogs";
import {
  useServerOrderList, OpsTabs, OpsSearch, OpsPagination, OrderCell, CustomerCell, PaymentCell, StatusPill, EmptyRow,
  ORDER_STATUS_LABEL, fmtDateTime,
} from "@/components/admin/orders/OrderOpsList";

const TABS = [
  { key: "requested", value: "REQUESTED", label: "Menunggu Persetujuan" },
  { key: "approved", value: "APPROVED", label: "Disetujui" },
  { key: "rejected", value: "REJECTED", label: "Ditolak" },
];

function RequestMeta({ o }) {
  const c = o.cancellation || {};
  const l = REQUEST_LABEL[c.request_status] || REQUEST_LABEL.REQUESTED;
  return (
    <div className="text-xs space-y-1 min-w-0">
      <StatusPill label={l.admin} cls={l.cls} testId={`ops-request-status-${o.order_number}`} />
      <div className="text-[#1A1A1A] break-words max-w-[280px]" data-testid={`ops-request-reason-${o.order_number}`}>“{c.request_reason || "-"}”</div>
      <div className="text-[#5C5C5C]">Diajukan {fmtDateTime(c.requested_at)}</div>
      {c.reviewed_at && (
        <div className="text-[#5C5C5C]" data-testid={`ops-request-review-${o.order_number}`}>
          Ditinjau {fmtDateTime(c.reviewed_at)}{c.payment_received != null ? ` · Dana diterima: ${c.payment_received ? "Ya" : "Tidak"}` : ""}
          {c.request_status === "REJECTED" && c.decision_note ? ` · Alasan: ${c.decision_note}` : ""}
        </div>
      )}
    </div>
  );
}

function RowActions({ o, canAct, onApprove, onReject }) {
  if (o.cancellation?.request_status !== "REQUESTED") return <Link to={`/admin/pesanan/${o.id}`} className="text-xs font-bold text-[#E85D04]" data-testid={`ops-detail-${o.order_number}`}>Detail →</Link>;
  if (!canAct) return <span className="text-xs text-[#5C5C5C]" data-testid={`ops-readonly-${o.order_number}`}>Hanya lihat</span>;
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" className="rounded-lg brand-btn-primary" onClick={() => onApprove(o)} data-testid={`ops-approve-${o.order_number}`}>Setujui</Button>
      <Button size="sm" variant="outline" className="rounded-lg text-red-600 border-red-200" onClick={() => onReject(o)} data-testid={`ops-reject-${o.order_number}`}>Tolak</Button>
    </div>
  );
}

export default function AdminOrderCancellations() {
  const { user } = useAuth();
  const canAct = hasPermission(user, "orders.update");
  const { data: attention, refresh: refreshAttention } = useOrderAttention();
  const list = useServerOrderList({ filterKey: "cancellation_request_status", tabs: TABS, defaultTab: "requested" });
  const [approveOf, setApproveOf] = useState(null);
  const [rejectOf, setRejectOf] = useState(null);
  const done = () => { setApproveOf(null); setRejectOf(null); list.reload(); refreshAttention(); };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl font-black" data-testid="cancellations-title">Pembatalan</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Pengajuan pembatalan dari pelanggan (pembayaran bank/QRIS). Pembatalan langsung oleh toko tidak masuk di sini.</p>
        </div>
        <Link to="/admin/pesanan" className="text-xs font-bold text-[#E85D04]" data-testid="cancellations-all-orders-link">Semua pesanan →</Link>
      </div>
      <OpsTabs tabs={TABS} value={list.tabKey} onChange={list.setTab} counts={{ requested: attention?.cancellation_requests || 0 }} testPrefix="cancellations" />

      <div className="bg-white border border-[#E5E5E5] rounded-2xl mt-4" data-testid="cancellations-list">
        <div className="p-4 border-b border-[#E5E5E5]"><OpsSearch value={list.q} onChange={list.setQ} testId="cancellations-search" /></div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                <th className="px-4 py-3">No Pesanan</th><th className="px-4 py-3">Pelanggan</th><th className="px-4 py-3">Pembayaran</th>
                <th className="px-4 py-3">Status Pesanan</th><th className="px-4 py-3">Pengajuan</th><th className="px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((o) => (
                <tr key={o.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6] align-top" data-testid={`ops-row-${o.order_number}`}>
                  <td className="px-4 py-3"><OrderCell o={o} /></td>
                  <td className="px-4 py-3"><CustomerCell o={o} /></td>
                  <td className="px-4 py-3"><PaymentCell o={o} /></td>
                  <td className="px-4 py-3"><StatusPill label={ORDER_STATUS_LABEL[o.status] || o.status} cls="bg-[#FFEDD5] text-[#E85D04]" /></td>
                  <td className="px-4 py-3"><RequestMeta o={o} /></td>
                  <td className="px-4 py-3"><RowActions o={o} canAct={canAct} onApprove={setApproveOf} onReject={setRejectOf} /></td>
                </tr>
              ))}
              {list.items.length === 0 && <EmptyRow loading={list.loading} colSpan={6} text="Tidak ada pengajuan pada tab ini" />}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-[#E5E5E5]" data-testid="cancellations-mobile-list">
          {list.items.map((o) => (
            <div key={o.id} className="p-4 space-y-3" data-testid={`ops-card-${o.order_number}`}>
              <div className="flex justify-between gap-3"><OrderCell o={o} /><StatusPill label={ORDER_STATUS_LABEL[o.status] || o.status} cls="bg-[#FFEDD5] text-[#E85D04]" /></div>
              <div className="grid grid-cols-2 gap-3"><CustomerCell o={o} /><PaymentCell o={o} /></div>
              <RequestMeta o={o} />
              <RowActions o={o} canAct={canAct} onApprove={setApproveOf} onReject={setRejectOf} />
            </div>
          ))}
          {list.items.length === 0 && <div className="py-12 text-center text-sm text-[#5C5C5C]">{list.loading ? "Memuat…" : "Tidak ada pengajuan pada tab ini"}</div>}
        </div>
        <OpsPagination page={list.page} total={list.total} onPage={list.setPage} testPrefix="cancellations" />
      </div>

      <ApproveCancellationDialog open={!!approveOf} order={approveOf} onClose={() => setApproveOf(null)} onDone={done} />
      <RejectCancellationDialog open={!!rejectOf} order={rejectOf} onClose={() => setRejectOf(null)} onDone={done} />
    </AdminLayout>
  );
}
